import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getBoards,
  getBoard,
  createBoard,
  updateBoard,
  deleteBoard,
  getMessages,
  createMessage,
  deleteMessage,
  getPhotos,
  uploadPhoto,
  deletePhoto,
  getMembers,
  addMember,
  removeMember,
  getActivity,
  getBoardHistory,
  getDisplayQueue,
  updateQueuePosition,
  removeFromQueue,
  queryKeys,
  type Board,
  type QueueRecord,
} from "@/lib/api";
import { usePromoteItem } from "@/hooks/use-promote-item";

// ─── Boards ──────────────────────────────────────────────────────

export function useBoards() {
  return useQuery({
    queryKey: queryKeys.boards,
    queryFn: async () => {
      const result = await getBoards();
      if (!result.ok) throw new Error(result.error);
      return result.data.boards;
    },
  });
}

export function useBoard(slug: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.board(slug),
    queryFn: async () => {
      const result = await getBoard(slug);
      if (!result.ok) throw new Error(result.error);
      return result.data.board;
    },
    enabled: !!slug && enabled,
  });
}

export function useCreateBoard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; slug: string; description?: string; public?: boolean }) =>
      createBoard(input),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.boards });
      toast.success("Board créé !");
    },
    onError: () => toast.error("Erreur lors de la création du board"),
  });
}

export function useUpdateBoard(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Pick<Board, "name" | "description" | "public" | "settings">>) =>
      updateBoard(slug, input),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.board(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.boards });
      toast.success("Board mis à jour");
    },
    onError: () => toast.error("Erreur lors de la mise à jour"),
  });
}

export function useDeleteBoard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => deleteBoard(slug),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.boards });
      toast.success("Board supprimé");
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });
}

// ─── Messages ────────────────────────────────────────────────────

export function useMessages(slug: string) {
  return useQuery({
    queryKey: queryKeys.messages(slug),
    queryFn: async () => {
      const result = await getMessages(slug);
      if (!result.ok) throw new Error(result.error);
      return result.data.messages;
    },
    enabled: !!slug,
  });
}

export function useCreateMessage(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ text, type }: { text: string; type?: string }) =>
      createMessage(slug, text, type),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.messages(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.boardHistory(slug) });
    },
    onError: () => toast.error("Erreur lors de l'envoi du message"),
  });
}

export function useDeleteMessage(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (msgId: string) => deleteMessage(slug, msgId),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.messages(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.boardHistory(slug) });
      toast.success("Message supprimé");
    },
  });
}

// ─── Photos ──────────────────────────────────────────────────────

export const PHOTOS_PER_PAGE = 50;

export function usePhotos(slug: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.photos(slug),
    queryFn: async ({ pageParam = 1 }) => {
      const result = await getPhotos(slug, pageParam, PHOTOS_PER_PAGE);
      if (!result.ok) throw new Error(result.error);
      return result.data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
    enabled: !!slug,
  });
}

export function useUploadPhoto(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, caption }: { file: File; caption?: string }) =>
      uploadPhoto(slug, file, caption),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.photos(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.boardHistory(slug) });
      toast.success("Photo ajoutée !");
    },
    onError: () => toast.error("Erreur lors de l'upload"),
  });
}

export function useDeletePhoto(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (photoId: string) => deletePhoto(slug, photoId),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.photos(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.boardHistory(slug) });
      toast.success("Photo supprimée");
    },
  });
}

export function usePromotePhoto(slug: string) {
  return usePromoteItem({
    collection: "photos",
    invalidateKey: queryKeys.photos(slug),
  });
}

// ─── Members ─────────────────────────────────────────────────────

export function useMembers(slug: string) {
  return useQuery({
    queryKey: queryKeys.members(slug),
    queryFn: async () => {
      const result = await getMembers(slug);
      if (!result.ok) throw new Error(result.error);
      return result.data.members;
    },
    enabled: !!slug,
  });
}

export function useAddMember(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role?: string }) =>
      addMember(slug, email, role),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.members(slug) });
      toast.success("Membre ajouté !");
    },
    onError: () => toast.error("Erreur lors de l'ajout du membre"),
  });
}

export function useRemoveMember(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => removeMember(slug, memberId),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.members(slug) });
      toast.success("Membre retiré");
    },
  });
}

// ─── Activity ─────────────────────────────────────────────────────

export function useActivity() {
  return useQuery({
    queryKey: queryKeys.activity,
    queryFn: async () => {
      const result = await getActivity();
      if (!result.ok) throw new Error(result.error);
      return result.data.items;
    },
  });
}

export function useBoardHistory(slug: string) {
  return useQuery({
    queryKey: queryKeys.boardHistory(slug),
    queryFn: async () => {
      const result = await getBoardHistory(slug);
      if (!result.ok) throw new Error(result.error);
      return result.data.items;
    },
    enabled: !!slug,
  });
}

// ─── Display Queue ────────────────────────────────────────────────

export function useDisplayQueue(slug: string) {
  return useQuery({
    queryKey: [...queryKeys.display(slug), "queue"],
    queryFn: async () => {
      const result = await getDisplayQueue(slug);
      if (!result.ok) throw new Error(result.error);
      return result.data;
    },
    enabled: !!slug,
    // Rafraîchissement court pour que la vue reste synchronisée sans realtime
    staleTime: 5_000,
  });
}

export function useUpdateQueuePosition(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, position }: { itemId: string; position: number }) =>
      updateQueuePosition(itemId, position),
    onSuccess: (result) => {
      if (!result.ok) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: [...queryKeys.display(slug), "queue"] });
    },
    onError: () => toast.error("Erreur lors du réordonnancement"),
  });
}

export function useRemoveFromQueue(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => removeFromQueue(itemId),
    onSuccess: (result) => {
      if (!result.ok) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: [...queryKeys.display(slug), "queue"] });
    },
    onError: () => toast.error("Impossible de retirer l'item"),
  });
}

export type { QueueRecord };
